/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WASP Frontend v2.5.0 - ScheduleList v3.0 (PROFESSIONAL COMPACT)
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file ScheduleList.tsx
 * @description Lista profesional compacta de envíos programados
 * @version 3.0.0
 * @date 2025-11-28
 * 
 * FEATURES v3.0:
 * - ✅ Diseño compacto profesional (estilo RecurringTab)
 * - ✅ Tabla de 7 columnas con paginación
 * - ✅ Búsqueda inteligente inline
 * - ✅ Filtros colapsables compactos
 * - ✅ Modales WindowManager style
 * - ✅ Color WhatsApp Light Green (#34E67E)
 * - ✅ Countdown en tiempo real
 * - ✅ Export CSV/JSON
 * - ✅ Responsive optimizado
 * - ✅ Zero Bootstrap alerts JS
 * 
 * CAMBIOS vs v2.0:
 * - ❌ Eliminados KPI Cards grandes
 * - ❌ Eliminada sección "Próximos 5 Envíos"
 * - ✅ Headers inline con badges compactos
 * - ✅ Tabla profesional con separadores claros
 * - ✅ Modales con z-index garantizado
 * - ✅ Paginación visible y funcional
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { 
  Badge, 
  Button, 
  ButtonGroup, 
  Form, 
  Row, 
  Col,
  Modal,
  Collapse,
  Card
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faBan,
  faTrash,
  faEye,
  faFilter,
  faTimes,
  faClock,
  faUsers,
  faCheckCircle,
  faTimesCircle,
  faExclamationTriangle,
  faCalendar,
  faDownload,
  faSearch,
  faFireAlt,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import {
  faWhatsapp,
  faInstagram,
  faFacebookF,
  faTelegram
} from '@fortawesome/free-brands-svg-icons';
import { ScheduledMessage, SchedulerStats, ScheduleStatus, ChannelType } from '../../../types/scheduler.types';
import { useScheduler } from '../../../hooks/useScheduler';
import { formatDateSpanish } from '../../../types/dateUtils';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface ScheduleListProps {
  onEditSchedule?: (schedule: ScheduledMessage) => void;
}

// ═══════════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Calcula el tiempo restante hasta el envío programado
 */
const getCountdown = (scheduledAt: string): { text: string; className: string; isUrgent: boolean } => {
  const now = new Date();
  const scheduled = new Date(scheduledAt);
  const diff = scheduled.getTime() - now.getTime();

  if (diff < 0) {
    return { text: 'Vencido', className: 'text-danger', isUrgent: false };
  }

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  // Formato legible
  if (days > 0) {
    const remainingHours = hours % 24;
    return { 
      text: `${days}d ${remainingHours}h`, 
      className: 'text-muted',
      isUrgent: false 
    };
  } else if (hours > 0) {
    const remainingMinutes = minutes % 60;
    return { 
      text: `${hours}h ${remainingMinutes}m`, 
      className: hours < 3 ? 'text-warning' : 'text-info',
      isUrgent: hours < 1
    };
  } else if (minutes > 0) {
    return { 
      text: `${minutes}min`, 
      className: 'text-warning',
      isUrgent: true
    };
  } else {
    return { 
      text: 'Ahora', 
      className: 'text-danger',
      isUrgent: true
    };
  }
};

/**
 * Export a CSV
 */
const exportToCSV = (schedules: ScheduledMessage[]) => {
  const headers = ['ID', 'Canal', 'Estado', 'Fecha', 'Hora', 'Destinatarios', 'Mensaje'];
  const rows = schedules.map(s => [
    s.id,
    s.channel,
    s.status,
    s.schedule.localDate,
    s.schedule.localTime,
    s.recipients.count,
    s.message.text.substring(0, 50).replace(/,/g, ';')
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `schedules_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Export a JSON
 */
const exportToJSON = (schedules: ScheduledMessage[]) => {
  const json = JSON.stringify(schedules, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `schedules_${new Date().toISOString().split('T')[0]}.json`;
  a.click();
  URL.revokeObjectURL(url);
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const ScheduleList: React.FC<ScheduleListProps> = ({ onEditSchedule }) => {
  // ═══════════════════════════════════════════════════════════════════════
  // HOOKS
  // ═══════════════════════════════════════════════════════════════════════
  
  const {
    schedules,
    stats,
    isLoading,
    error,
    filters,
    setFilters,
    cancelSchedule,
    deleteSchedule,
    refreshSchedules
  } = useScheduler();
  
  // ═══════════════════════════════════════════════════════════════════════
  // LOCAL STATE
  // ═══════════════════════════════════════════════════════════════════════
  
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduledMessage | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Modales de confirmación
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [scheduleToAction, setScheduleToAction] = useState<string | null>(null);
  
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Búsqueda local
  const [searchTerm, setSearchTerm] = useState('');
  
  // Countdown auto-update
  const [, setCountdownTick] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCountdownTick(tick => tick + 1);
    }, 1000); // Actualizar cada segundo
    
    return () => clearInterval(interval);
  }, []);

  // Inject modal styles for high z-index
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .modal-backdrop-ultra-high {
        z-index: 999998 !important;
        background-color: rgba(0, 0, 0, 0.7) !important;
      }
      .modal-ultra-high {
        z-index: 999999 !important;
      }
      .modal-ultra-high .modal-dialog {
        z-index: 1000000 !important;
        position: relative;
      }
      .modal-ultra-high .modal-content {
        z-index: 1000001 !important;
        position: relative;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // ═══════════════════════════════════════════════════════════════════════
  // COMPUTED STATS FROM SCHEDULES
  // ═══════════════════════════════════════════════════════════════════════
  
  const computedStats = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    return {
      totalScheduled: schedules.length,
      pendingCount: schedules.filter(s => s.status === 'pending').length,
      upcomingToday: schedules.filter(s => {
        const scheduleDate = new Date(s.schedule.scheduledAt);
        return s.status === 'pending' && 
               scheduleDate.getFullYear() === today.getFullYear() &&
               scheduleDate.getMonth() === today.getMonth() &&
               scheduleDate.getDate() === today.getDate();
      }).length,
      completedCount: schedules.filter(s => s.status === 'sent').length
    };
  }, [schedules]);

  // ═══════════════════════════════════════════════════════════════════════
  // FILTERED DATA
  // ═══════════════════════════════════════════════════════════════════════
  
  const filteredSchedules = useMemo(() => {
    let result = schedules;
    
    // Filtro por status
    if (filters.status && filters.status.length > 0) {
      result = result.filter(s => filters.status!.includes(s.status));
    }
    
    // Filtro por canal
    if (filters.channel && filters.channel.length > 0) {
      result = result.filter(s => filters.channel!.includes(s.channel));
    }
    
    // Búsqueda por texto
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(s => 
        s.message.text.toLowerCase().includes(searchLower) ||
        s.campaignName?.toLowerCase().includes(searchLower) ||
        s.id.toLowerCase().includes(searchLower) ||
        s.channel.toLowerCase().includes(searchLower)
      );
    }
    
    return result;
  }, [schedules, filters, searchTerm]);

  // Paginación
  const totalPages = Math.ceil(filteredSchedules.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSchedules = filteredSchedules.slice(startIndex, endIndex);

  // Reset página cuando cambia filtro o búsqueda
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);

  // ═══════════════════════════════════════════════════════════════════════
  // STATUS CONFIG
  // ═══════════════════════════════════════════════════════════════════════
  
  const statusConfig: Record<ScheduleStatus, { label: string; color: string; bg: string; icon: any }> = {
    pending: { label: 'Pendiente', color: '#f59e0b', bg: 'warning', icon: faClock },
    processing: { label: 'Procesando', color: '#3b82f6', bg: 'primary', icon: faExclamationTriangle },
    sent: { label: 'Enviado', color: '#10b981', bg: 'success', icon: faCheckCircle },
    failed: { label: 'Fallido', color: '#ef4444', bg: 'danger', icon: faTimesCircle },
    cancelled: { label: 'Cancelado', color: '#6b7280', bg: 'secondary', icon: faBan }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // CHANNEL CONFIG
  // ═══════════════════════════════════════════════════════════════════════
  
  const channelConfig: Record<ChannelType, { icon: any; color: string; label: string }> = {
    whatsapp: { icon: faWhatsapp, color: '#34E67E', label: 'WhatsApp' },
    instagram: { icon: faInstagram, color: '#E4405F', label: 'Instagram' },
    facebook: { icon: faFacebookF, color: '#1877F2', label: 'Facebook' },
    telegram: { icon: faTelegram, color: '#0088CC', label: 'Telegram' }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════════════
  
  const handleCancelSchedule = async (id: string) => {
    setScheduleToAction(id);
    setShowCancelModal(true);
  };

  const confirmCancelSchedule = async () => {
    if (!scheduleToAction) return;
    
    try {
      await cancelSchedule(scheduleToAction);
      await refreshSchedules();
      setShowCancelModal(false);
      setScheduleToAction(null);
    } catch (error) {
      console.error('Error canceling schedule:', error);
    }
  };

  const handleDeleteSchedule = async (id: string) => {
    setScheduleToAction(id);
    setShowDeleteModal(true);
  };

  const confirmDeleteSchedule = async () => {
    if (!scheduleToAction) return;
    
    try {
      await deleteSchedule(scheduleToAction);
      await refreshSchedules();
      setShowDeleteModal(false);
      setScheduleToAction(null);
    } catch (error) {
      console.error('Error deleting schedule:', error);
    }
  };

  const handleStatusFilter = (status: ScheduleStatus) => {
    const currentStatuses = (filters.status || []) as ScheduleStatus[];
    const newStatuses = currentStatuses.includes(status)
      ? currentStatuses.filter(s => s !== status)
      : [...currentStatuses, status];
    
    setFilters({ ...filters, status: newStatuses });
  };

  const handleChannelFilter = (channel: ChannelType) => {
    const currentChannels = (filters.channel || []) as ChannelType[];
    const newChannels = currentChannels.includes(channel)
      ? currentChannels.filter(c => c !== channel)
      : [...currentChannels, channel];
    
    setFilters({ ...filters, channel: newChannels });
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const hasActiveFilters = 
    (filters.status && filters.status.length > 0) ||
    (filters.channel && filters.channel.length > 0) ||
    searchTerm;

  const handleViewDetail = (schedule: ScheduledMessage) => {
    setSelectedSchedule(schedule);
    setShowDetailModal(true);
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER KPI CARDS
  // ═══════════════════════════════════════════════════════════════════════
  
  const renderKPICards = () => {
    return (
      <Row className="mb-4">
        <Col xs={12} md={3}>
          <Card
            className="text-center"
            style={{
              borderRadius: '12px',
              border: '1px solid var(--bs-border-color)',
              backgroundColor: 'var(--bs-body-bg)',
              cursor: 'pointer',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <Card.Body>
              <FontAwesomeIcon 
                icon={faCalendar} 
                size="2x" 
                style={{ color: '#34E67E', marginBottom: '8px' }} 
              />
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--bs-body-color)' }}>
                {computedStats.totalScheduled}
              </div>
              <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary-color)' }}>
                Total Programados
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={3}>
          <Card
            className="text-center"
            style={{
              borderRadius: '12px',
              border: '1px solid var(--bs-border-color)',
              backgroundColor: 'var(--bs-body-bg)',
              cursor: 'pointer',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <Card.Body>
              <FontAwesomeIcon 
                icon={faClock} 
                size="2x" 
                style={{ color: '#f59e0b', marginBottom: '8px' }} 
              />
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f59e0b' }}>
                {computedStats.pendingCount}
              </div>
              <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary-color)' }}>
                Pendientes
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={3}>
          <Card
            className="text-center"
            style={{
              borderRadius: '12px',
              border: '1px solid var(--bs-border-color)',
              backgroundColor: 'var(--bs-body-bg)',
              cursor: 'pointer',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <Card.Body>
              <FontAwesomeIcon 
                icon={faFireAlt} 
                size="2x" 
                style={{ color: '#ef4444', marginBottom: '8px' }} 
              />
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ef4444' }}>
                {computedStats.upcomingToday}
              </div>
              <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary-color)' }}>
                Programados Hoy
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={3}>
          <Card
            className="text-center"
            style={{
              borderRadius: '12px',
              border: '1px solid var(--bs-border-color)',
              backgroundColor: 'var(--bs-body-bg)',
              cursor: 'pointer',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <Card.Body>
              <FontAwesomeIcon 
                icon={faCheckCircle} 
                size="2x" 
                style={{ color: '#10b981', marginBottom: '8px' }} 
              />
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>
                {computedStats.completedCount}
              </div>
              <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary-color)' }}>
                Completados
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER HEADER COMPACTO
  // ═══════════════════════════════════════════════════════════════════════
  
  const renderHeader = () => {
    return (
      <div 
        className="d-flex justify-content-between align-items-center mb-3 p-3"
        style={{
          backgroundColor: 'var(--bs-body-bg)',
          borderRadius: '12px',
          border: '1px solid var(--bs-border-color)'
        }}
      >
        <h6 className="mb-0" style={{ color: 'var(--bs-body-color)', fontWeight: 600, fontSize: '15px' }}>
          Envíos Programados
        </h6>

        {/* Actions */}
        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            style={{
              borderRadius: '8px',
              fontSize: '13px',
              padding: '6px 12px'
            }}
          >
            <FontAwesomeIcon icon={faFilter} className="me-1" />
            Filtros
            <FontAwesomeIcon 
              icon={showFilters ? faChevronUp : faChevronDown} 
              className="ms-1" 
              style={{ fontSize: '10px' }}
            />
          </Button>
          
          <ButtonGroup size="sm">
            <Button
              variant="outline-secondary"
              onClick={() => exportToCSV(filteredSchedules)}
              title="Exportar CSV"
              style={{
                borderRadius: '8px 0 0 8px',
                fontSize: '13px',
                padding: '6px 12px'
              }}
            >
              <FontAwesomeIcon icon={faDownload} className="me-1" />
              CSV
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => exportToJSON(filteredSchedules)}
              title="Exportar JSON"
              style={{
                borderRadius: '0 8px 8px 0',
                fontSize: '13px',
                padding: '6px 12px'
              }}
            >
              JSON
            </Button>
          </ButtonGroup>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER FILTROS COMPACTOS COLAPSABLES
  // ═══════════════════════════════════════════════════════════════════════
  
  const renderFilters = () => {
    return (
      <Collapse in={showFilters}>
        <div 
          className="mb-3 p-3"
          style={{
            backgroundColor: 'var(--bs-body-bg)',
            borderRadius: '12px',
            border: '1px solid var(--bs-border-color)'
          }}
        >
          <Row className="g-3">
            {/* Status Filter */}
            <Col md={6}>
              <Form.Label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--bs-secondary-color)', marginBottom: '6px' }}>
                ESTADO
              </Form.Label>
              <div className="d-flex gap-2 flex-wrap">
                {(Object.keys(statusConfig) as ScheduleStatus[]).map(status => (
                  <Badge
                    key={status}
                    bg="none"
                    style={{
                      backgroundColor: (filters.status && filters.status.includes(status))
                        ? statusConfig[status].color 
                        : 'var(--bs-secondary-bg)',
                      color: (filters.status && filters.status.includes(status)) ? 'white' : 'var(--bs-body-color)',
                      border: `1px solid ${(filters.status && filters.status.includes(status)) ? statusConfig[status].color : 'var(--bs-border-color)'}`,
                      cursor: 'pointer',
                      padding: '4px 10px',
                      fontSize: '11px',
                      fontWeight: 500
                    }}
                    onClick={() => handleStatusFilter(status)}
                  >
                    <FontAwesomeIcon icon={statusConfig[status].icon} style={{ fontSize: '10px' }} className="me-1" />
                    {statusConfig[status].label}
                  </Badge>
                ))}
              </div>
            </Col>

            {/* Channel Filter */}
            <Col md={6}>
              <Form.Label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--bs-secondary-color)', marginBottom: '6px' }}>
                CANAL
              </Form.Label>
              <div className="d-flex gap-2 flex-wrap">
                {(Object.keys(channelConfig) as ChannelType[]).map(channel => (
                  <Badge
                    key={channel}
                    bg="none"
                    style={{
                      backgroundColor: (filters.channel && filters.channel.includes(channel))
                        ? `${channelConfig[channel].color}20`
                        : 'var(--bs-secondary-bg)',
                      color: (filters.channel && filters.channel.includes(channel)) 
                        ? channelConfig[channel].color 
                        : 'var(--bs-body-color)',
                      border: `1px solid ${(filters.channel && filters.channel.includes(channel)) ? channelConfig[channel].color : 'var(--bs-border-color)'}`,
                      cursor: 'pointer',
                      padding: '4px 10px',
                      fontSize: '11px',
                      fontWeight: 500
                    }}
                    onClick={() => handleChannelFilter(channel)}
                  >
                    <FontAwesomeIcon icon={channelConfig[channel].icon} style={{ fontSize: '10px' }} className="me-1" />
                    {channelConfig[channel].label}
                  </Badge>
                ))}
              </div>
            </Col>
          </Row>

          {hasActiveFilters && (
            <div className="mt-2">
              <Button
                variant="link"
                size="sm"
                onClick={clearFilters}
                style={{ 
                  color: '#ef4444', 
                  padding: 0,
                  fontSize: '12px',
                  textDecoration: 'none'
                }}
              >
                <FontAwesomeIcon icon={faTimes} className="me-1" />
                Limpiar todos los filtros
              </Button>
            </div>
          )}
        </div>
      </Collapse>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER TABLE PROFESIONAL
  // ═══════════════════════════════════════════════════════════════════════
  
  const renderTable = () => {
    if (isLoading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border" style={{ color: '#34E67E' }} role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      );
    }

    if (filteredSchedules.length === 0) {
      return (
        <div 
          className="text-center py-5"
          style={{
            backgroundColor: 'var(--bs-body-bg)',
            borderRadius: '12px',
            border: '1px solid var(--bs-border-color)'
          }}
        >
          <FontAwesomeIcon 
            icon={faClock} 
            style={{ fontSize: '48px', color: 'var(--bs-secondary-color)', marginBottom: '16px' }} 
          />
          <p style={{ color: 'var(--bs-secondary-color)', fontSize: '14px', marginBottom: 0 }}>
            {hasActiveFilters 
              ? 'No se encontraron envíos que coincidan con los filtros'
              : 'No hay envíos programados'}
          </p>
        </div>
      );
    }

    return (
      <div>
        {/* Buscador inline + Contador y selector de items */}
        <div className="d-flex justify-content-between align-items-center mb-2 px-2 flex-wrap gap-2">
          {/* Buscador */}
          <div className="input-group" style={{ maxWidth: '350px', flexGrow: 1 }}>
            <span className="input-group-text" style={{ backgroundColor: 'var(--bs-body-bg)', border: '1px solid var(--bs-border-color)' }}>
              <FontAwesomeIcon icon={faSearch} style={{ color: 'var(--bs-secondary-color)', fontSize: '12px' }} />
            </span>
            <Form.Control
              type="text"
              placeholder="Buscar por mensaje, campaña, ID o canal..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="sm"
              style={{ 
                fontSize: '12px',
                border: '1px solid var(--bs-border-color)',
                borderLeft: 'none'
              }}
            />
            {searchTerm && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setSearchTerm('')}
                style={{ fontSize: '12px', padding: '4px 8px' }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            )}
          </div>

          {/* Contador y selector */}
          <div className="d-flex align-items-center gap-3">
            <small style={{ color: 'var(--bs-secondary-color)', fontSize: '12px', whiteSpace: 'nowrap' }}>
              Mostrando {startIndex + 1}-{Math.min(endIndex, filteredSchedules.length)} de {filteredSchedules.length} envíos
            </small>
            
            <div className="d-flex align-items-center gap-2">
              <small style={{ color: 'var(--bs-secondary-color)', fontSize: '12px' }}>Por página:</small>
              <Form.Select
                size="sm"
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                style={{ 
                  width: 'auto',
                  fontSize: '12px',
                  padding: '4px 8px'
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </Form.Select>
            </div>
          </div>
        </div>

        {/* Tabla */}
        <div 
          className="table-responsive"
          style={{
            backgroundColor: 'var(--bs-body-bg)',
            borderRadius: '12px',
            border: '1px solid var(--bs-border-color)',
            overflow: 'hidden'
          }}
        >
          <table className="table table-hover mb-0" style={{ fontSize: '13px' }}>
            <thead>
              <tr style={{ 
                backgroundColor: '#f9fafb',
                borderBottom: '2px solid #e5e7eb'
              }}>
                <th style={{ 
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: '#6b7280',
                  padding: '10px 12px',
                  width: '60px'
                }}>
                  Canal
                </th>
                <th style={{ 
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: '#6b7280',
                  padding: '10px 12px',
                  minWidth: '250px'
                }}>
                  Mensaje
                </th>
                <th style={{ 
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: '#6b7280',
                  padding: '10px 12px',
                  width: '140px'
                }}>
                  Programado
                </th>
                <th style={{ 
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: '#6b7280',
                  padding: '10px 12px',
                  width: '110px'
                }}>
                  Countdown
                </th>
                <th style={{ 
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: '#6b7280',
                  padding: '10px 12px',
                  width: '80px',
                  textAlign: 'center'
                }}>
                  Dest.
                </th>
                <th style={{ 
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: '#6b7280',
                  padding: '10px 12px',
                  width: '110px'
                }}>
                  Estado
                </th>
                <th style={{ 
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: '#6b7280',
                  padding: '10px 12px',
                  width: '160px',
                  textAlign: 'right'
                }}>
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedSchedules.map(schedule => {
                const status = statusConfig[schedule.status];
                const channel = channelConfig[schedule.channel];
                const countdown = getCountdown(schedule.schedule.scheduledAt);
                
                return (
                  <tr 
                    key={schedule.id}
                    style={{
                      borderBottom: '1px solid #f3f4f6',
                      opacity: schedule.status === 'cancelled' ? 0.6 : 1
                    }}
                  >
                    {/* Canal */}
                    <td style={{ padding: '10px 12px' }}>
                      <div
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          backgroundColor: `${channel.color}20`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: `1px solid ${channel.color}`
                        }}
                        title={channel.label}
                      >
                        <FontAwesomeIcon
                          icon={channel.icon}
                          style={{ color: channel.color, fontSize: '14px' }}
                        />
                      </div>
                    </td>

                    {/* Mensaje */}
                    <td style={{ padding: '10px 12px' }}>
                      <div>
                        {schedule.campaignName && (
                          <div style={{ 
                            fontSize: '12px', 
                            fontWeight: 600, 
                            color: '#34E67E',
                            marginBottom: '2px'
                          }}>
                            {schedule.campaignName}
                          </div>
                        )}
                        <div
                          style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '300px',
                            color: 'var(--bs-body-color)',
                            fontSize: '12px'
                          }}
                          title={schedule.message.text}
                        >
                          {schedule.message.text}
                        </div>
                      </div>
                    </td>

                    {/* Programado */}
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ fontSize: '12px' }}>
                        <div style={{ fontWeight: 500, color: 'var(--bs-body-color)' }}>
                          {formatDateSpanish(schedule.schedule.localDate)}
                        </div>
                        <div style={{ color: 'var(--bs-secondary-color)', fontSize: '11px', marginTop: '2px' }}>
                          <FontAwesomeIcon icon={faClock} className="me-1" style={{ fontSize: '10px' }} />
                          {schedule.schedule.localTime}
                        </div>
                      </div>
                    </td>

                    {/* Countdown */}
                    <td style={{ padding: '10px 12px' }}>
                      {schedule.status === 'pending' ? (
                        <span className={countdown.className} style={{ fontSize: '12px', fontWeight: 600 }}>
                          {countdown.isUrgent && (
                            <FontAwesomeIcon icon={faFireAlt} className="me-1 pulse-animation" style={{ fontSize: '11px' }} />
                          )}
                          {countdown.text}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--bs-secondary-color)', fontSize: '12px' }}>-</span>
                      )}
                    </td>

                    {/* Destinatarios */}
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <Badge bg="light" text="dark">
                        <FontAwesomeIcon icon={faUsers} style={{ fontSize: '10px' }} className="me-1" />
                        {schedule.recipients.count}
                      </Badge>
                    </td>

                    {/* Estado */}
                    <td style={{ padding: '10px 12px' }}>
                      <Badge bg={status.bg}>
                        <FontAwesomeIcon icon={status.icon} style={{ fontSize: '10px' }} className="me-1" />
                        {status.label}
                      </Badge>
                    </td>

                    {/* Acciones */}
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                      <div className="btn-group btn-group-sm" role="group">
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => handleViewDetail(schedule)}
                          title="Ver detalle"
                          style={{
                            fontSize: '11px',
                            padding: '4px 8px',
                            borderRadius: '6px 0 0 6px'
                          }}
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                        
                        {schedule.status === 'pending' && (
                          <>
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => onEditSchedule?.(schedule)}
                              title="Editar"
                              style={{
                                fontSize: '11px',
                                padding: '4px 8px',
                                borderColor: '#34E67E',
                                color: '#34E67E'
                              }}
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button
                              className="btn btn-outline-warning"
                              onClick={() => handleCancelSchedule(schedule.id)}
                              title="Cancelar"
                              style={{
                                fontSize: '11px',
                                padding: '4px 8px'
                              }}
                            >
                              <FontAwesomeIcon icon={faBan} />
                            </button>
                          </>
                        )}
                        
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => handleDeleteSchedule(schedule.id)}
                          title="Eliminar"
                          style={{
                            fontSize: '11px',
                            padding: '4px 8px',
                            borderRadius: '0 6px 6px 0'
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              style={{ fontSize: '12px', padding: '4px 12px' }}
            >
              Anterior
            </Button>

            {[...Array(totalPages)].map((_, index) => {
              const page = index + 1;
              // Mostrar solo páginas cercanas
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <Button
                    key={page}
                    variant={currentPage === page ? 'primary' : 'outline-secondary'}
                    size="sm"
                    onClick={() => setCurrentPage(page)}
                    style={{
                      fontSize: '12px',
                      padding: '4px 12px',
                      minWidth: '32px',
                      ...(currentPage === page && {
                        backgroundColor: '#34E67E',
                        borderColor: '#34E67E'
                      })
                    }}
                  >
                    {page}
                  </Button>
                );
              } else if (page === currentPage - 2 || page === currentPage + 2) {
                return (
                  <span key={page} style={{ color: 'var(--bs-secondary-color)', fontSize: '12px' }}>
                    ...
                  </span>
                );
              }
              return null;
            })}

            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              style={{ fontSize: '12px', padding: '4px 12px' }}
            >
              Siguiente
            </Button>

            <span style={{ marginLeft: '8px', fontSize: '12px', color: 'var(--bs-secondary-color)' }}>
              Página {currentPage} de {totalPages}
            </span>
          </div>
        )}
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER DETAIL MODAL COMPACTO
  // ═══════════════════════════════════════════════════════════════════════
  
  const renderDetailModal = () => {
    if (!selectedSchedule) return null;

    const status = statusConfig[selectedSchedule.status];
    const channel = channelConfig[selectedSchedule.channel];

    const modalContent = (
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        centered
        size="lg"
        className="modal-ultra-high"
        backdropClassName="modal-backdrop-ultra-high"
        style={{ zIndex: 999999 }}
        container={document.body}
      >
        <Modal.Header
          closeButton
          style={{
            backgroundColor: '#34E67E',
            color: 'white',
            borderRadius: '12px 12px 0 0',
            padding: '12px 20px',
            border: 'none'
          }}
        >
          <Modal.Title style={{ fontSize: '14px', fontWeight: 600 }}>
            <FontAwesomeIcon icon={faEye} className="me-2" />
            Detalle del Envío Programado
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body style={{ 
          padding: '20px',
          maxHeight: '70vh',
          overflowY: 'auto',
          fontSize: '13px'
        }}>
          <Row className="mb-3">
            <Col md={6}>
              <div className="mb-2">
                <strong style={{ color: 'var(--bs-secondary-color)', fontSize: '11px', textTransform: 'uppercase' }}>
                  Estado:
                </strong>
                <div className="mt-1">
                  <Badge bg={status.bg}>
                    <FontAwesomeIcon icon={status.icon} className="me-1" />
                    {status.label}
                  </Badge>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-2">
                <strong style={{ color: 'var(--bs-secondary-color)', fontSize: '11px', textTransform: 'uppercase' }}>
                  Canal:
                </strong>
                <div className="mt-1">
                  <Badge
                    style={{
                      backgroundColor: `${channel.color}20`,
                      color: channel.color,
                      border: `1px solid ${channel.color}`,
                      fontSize: '11px',
                      padding: '4px 10px'
                    }}
                  >
                    <FontAwesomeIcon icon={channel.icon} className="me-1" />
                    {channel.label}
                  </Badge>
                </div>
              </div>
            </Col>
          </Row>

          {selectedSchedule.campaignName && (
            <div className="mb-3">
              <strong style={{ color: 'var(--bs-secondary-color)', fontSize: '11px', textTransform: 'uppercase' }}>
                Campaña:
              </strong>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#34E67E', marginTop: '4px' }}>
                {selectedSchedule.campaignName}
              </div>
            </div>
          )}

          <div className="mb-3">
            <strong style={{ color: 'var(--bs-secondary-color)', fontSize: '11px', textTransform: 'uppercase' }}>
              Programado para:
            </strong>
            <div style={{ fontSize: '13px', marginTop: '4px' }}>
              <FontAwesomeIcon icon={faCalendar} className="me-2" style={{ color: 'var(--bs-secondary-color)' }} />
              {formatDateSpanish(selectedSchedule.schedule.localDate)} a las {selectedSchedule.schedule.localTime}
              <Badge bg="secondary" className="ms-2" style={{ fontSize: '10px' }}>
                {selectedSchedule.schedule.timezone}
              </Badge>
            </div>
          </div>

          <div className="mb-3">
            <strong style={{ color: 'var(--bs-secondary-color)', fontSize: '11px', textTransform: 'uppercase' }}>
              Destinatarios:
            </strong>
            <div style={{ fontSize: '13px', marginTop: '4px' }}>
              <FontAwesomeIcon icon={faUsers} className="me-2" style={{ color: 'var(--bs-secondary-color)' }} />
              {selectedSchedule.recipients.count} contacto(s)
            </div>
          </div>

          <div className="mb-3">
            <strong style={{ color: 'var(--bs-secondary-color)', fontSize: '11px', textTransform: 'uppercase' }}>
              Mensaje:
            </strong>
            <div
              style={{
                backgroundColor: 'var(--bs-secondary-bg)',
                padding: '12px',
                borderRadius: '8px',
                fontSize: '13px',
                whiteSpace: 'pre-wrap',
                border: '1px solid var(--bs-border-color)',
                marginTop: '4px'
              }}
            >
              {selectedSchedule.message.text}
            </div>
          </div>

          <Row>
            <Col md={6}>
              <div className="mb-2">
                <strong style={{ color: 'var(--bs-secondary-color)', fontSize: '11px', textTransform: 'uppercase' }}>
                  Creado:
                </strong>
                <div style={{ fontSize: '12px', marginTop: '4px', color: 'var(--bs-body-color)' }}>
                  {formatDateSpanish(selectedSchedule.metadata.createdAt, true)}
                </div>
              </div>
            </Col>
            {selectedSchedule.metadata.sentAt && (
              <Col md={6}>
                <div className="mb-2">
                  <strong style={{ color: 'var(--bs-secondary-color)', fontSize: '11px', textTransform: 'uppercase' }}>
                    Enviado:
                  </strong>
                  <div style={{ fontSize: '12px', marginTop: '4px', color: 'var(--bs-body-color)' }}>
                    {formatDateSpanish(selectedSchedule.metadata.sentAt, true)}
                  </div>
                </div>
              </Col>
            )}
          </Row>

          {selectedSchedule.metadata.failedReason && (
            <div 
              className="mt-3 p-2"
              style={{
                backgroundColor: '#ef444420',
                border: '1px solid #ef4444',
                borderRadius: '8px',
                fontSize: '12px'
              }}
            >
              <FontAwesomeIcon icon={faTimesCircle} className="me-2" style={{ color: '#ef4444' }} />
              <strong style={{ color: '#ef4444' }}>Error:</strong> {selectedSchedule.metadata.failedReason}
            </div>
          )}
        </Modal.Body>
        
        <Modal.Footer style={{ 
          backgroundColor: 'var(--bs-body-bg)', 
          borderTop: '1px solid var(--bs-border-color)',
          padding: '12px 20px',
          gap: '8px'
        }}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowDetailModal(false)}
            style={{ 
              borderRadius: '8px',
              fontSize: '13px',
              padding: '6px 16px'
            }}
          >
            Cerrar
          </Button>
          {selectedSchedule.status === 'pending' && (
            <Button
              size="sm"
              onClick={() => {
                setShowDetailModal(false);
                onEditSchedule?.(selectedSchedule);
              }}
              style={{ 
                borderRadius: '8px',
                backgroundColor: '#34E67E',
                borderColor: '#34E67E',
                fontSize: '13px',
                padding: '6px 16px'
              }}
            >
              <FontAwesomeIcon icon={faEdit} className="me-1" />
              Editar
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    );

    return createPortal(modalContent, document.body);
  };

  // ═══════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════════════════
  
  return (
    <div>
      {/* KPI Cards */}
      {renderKPICards()}

      {/* Header Compacto */}
      {renderHeader()}

      {/* Filtros Colapsables */}
      {renderFilters()}

      {/* Tabla Profesional */}
      {renderTable()}

      {/* Detail Modal */}
      {renderDetailModal()}

      {/* Cancel Confirmation Modal */}
      {createPortal(
        <Modal
          show={showCancelModal}
          onHide={() => setShowCancelModal(false)}
          centered
          size="sm"
          className="modal-ultra-high"
          backdropClassName="modal-backdrop-ultra-high"
          style={{ zIndex: 999999 }}
          container={document.body}
        >
          <Modal.Header
            closeButton
            style={{
              backgroundColor: '#34E67E',
              color: 'white',
              borderRadius: '12px 12px 0 0',
              padding: '12px 20px',
              border: 'none'
            }}
          >
            <Modal.Title style={{ fontSize: '14px', fontWeight: 600 }}>
              <FontAwesomeIcon icon={faBan} className="me-2" />
              Cancelar Envío
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ 
            padding: '20px',
            fontSize: '13px'
          }}>
            <p style={{ marginBottom: '12px' }}>
              ¿Estás seguro de que deseas cancelar este envío programado?
            </p>
            <div
              style={{
                backgroundColor: '#f59e0b20',
                border: '1px solid #f59e0b',
                borderRadius: '8px',
                padding: '8px 12px',
                fontSize: '12px',
                marginBottom: 0
              }}
            >
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" style={{ color: '#f59e0b' }} />
              El envío quedará marcado como <strong>cancelado</strong>.
            </div>
          </Modal.Body>
          <Modal.Footer style={{ 
            backgroundColor: 'var(--bs-body-bg)', 
            borderTop: '1px solid var(--bs-border-color)',
            padding: '12px 20px',
            gap: '8px'
          }}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowCancelModal(false)}
              style={{ 
                borderRadius: '8px',
                fontSize: '13px',
                padding: '6px 16px'
              }}
            >
              No, mantener
            </Button>
            <Button
              variant="warning"
              size="sm"
              onClick={confirmCancelSchedule}
              style={{ 
                borderRadius: '8px',
                fontSize: '13px',
                padding: '6px 16px'
              }}
            >
              <FontAwesomeIcon icon={faBan} className="me-1" />
              Sí, cancelar
            </Button>
          </Modal.Footer>
        </Modal>,
        document.body
      )}

      {/* Delete Confirmation Modal */}
      {createPortal(
        <Modal
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          centered
          size="sm"
          className="modal-ultra-high"
          backdropClassName="modal-backdrop-ultra-high"
          style={{ zIndex: 999999 }}
          container={document.body}
        >
          <Modal.Header
            closeButton
            style={{
              backgroundColor: '#34E67E',
              color: 'white',
              borderRadius: '12px 12px 0 0',
              padding: '12px 20px',
              border: 'none'
            }}
          >
            <Modal.Title style={{ fontSize: '14px', fontWeight: 600 }}>
              <FontAwesomeIcon icon={faTrash} className="me-2" />
              Eliminar Envío
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ 
            padding: '20px',
            fontSize: '13px'
          }}>
            <p style={{ marginBottom: '12px' }}>
              ¿Estás seguro de que deseas eliminar este envío programado?
            </p>
            <div
              style={{
                backgroundColor: '#ef444420',
                border: '1px solid #ef4444',
                borderRadius: '8px',
                padding: '8px 12px',
                fontSize: '12px',
                marginBottom: 0
              }}
            >
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" style={{ color: '#ef4444' }} />
              <strong>Esta acción no se puede deshacer.</strong>
            </div>
          </Modal.Body>
          <Modal.Footer style={{ 
            backgroundColor: 'var(--bs-body-bg)', 
            borderTop: '1px solid var(--bs-border-color)',
            padding: '12px 20px',
            gap: '8px'
          }}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowDeleteModal(false)}
              style={{ 
                borderRadius: '8px',
                fontSize: '13px',
                padding: '6px 16px'
              }}
            >
              No, mantener
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={confirmDeleteSchedule}
              style={{ 
                borderRadius: '8px',
                fontSize: '13px',
                padding: '6px 16px'
              }}
            >
              <FontAwesomeIcon icon={faTrash} className="me-1" />
              Sí, eliminar
            </Button>
          </Modal.Footer>
        </Modal>,
        document.body
      )}

      {/* CSS for pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .pulse-animation {
          animation: pulse 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default ScheduleList;